import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Video as VideoIcon, Plus, Edit, Trash2, Save, Loader2, Youtube, Film, Upload, Image as ImageIcon } from 'lucide-react';
import FileUploadInput from '../../components/admin/FileUploadInput';
import { getImageUrl } from '../../utils/assetUrlHelper';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VideosManagementPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    video_upload: '',
    video_url: '',
    thumbnail_image: '',
    title: '',
    description: '',
    tags: [],
    captions_file: '',
    published: true,
    order: 0
  });

  const [newTag, setNewTag] = useState('');

  // File upload states
  // File upload states
  const [fileData, setFileData] = useState({});

  useEffect(() => {
    fetchVideos();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/media/videos?published_only=false`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingVideo(null);
    setForm({
      video_upload: '',
      video_url: '',
      thumbnail_image: '',
      title: '',
      description: '',
      tags: [],
      captions_file: '',
      published: true,
      order: 0
    });
    setOpenDialog(true);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setForm({
      video_upload: video.video_upload || '',
      video_url: video.video_url || '',
      thumbnail_image: video.thumbnail_image,
      title: video.title,
      description: video.description || '',
      tags: video.tags || [],
      captions_file: video.captions_file || '',
      published: video.published,
      order: video.order
    });
    setOpenDialog(true);
  };

  const uploadFile = async (file, subfolder) => {
    const formData = new FormData();
    formData.append('file', file);
    // Use query parameter for subfolder to match backend API
    const res = await axios.post(`${API_URL}/api/files/upload?subfolder=${subfolder}`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${res.data.id}`;
  };

  const handleSave = async () => {
    // Validation
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      setSaving(true);
      let updatedForm = { ...form };

      if (fileData.video_file) {
        const url = await uploadFile(fileData.video_file, 'videos/files');
        updatedForm.video_upload = url;
        updatedForm.video_url = '';
      }

      if (fileData.thumbnail_file) {
        const url = await uploadFile(fileData.thumbnail_file, 'videos/thumbnails');
        updatedForm.thumbnail_image = url;
      }

      if (fileData.captions_file) {
        const url = await uploadFile(fileData.captions_file, 'videos/captions');
        updatedForm.captions_file = url;
      }

      if (!updatedForm.video_upload && !updatedForm.video_url) {
        toast.error('Either upload a video file or provide a video URL');
        setSaving(false);
        return;
      }

      if (!updatedForm.thumbnail_image) {
        toast.error('Thumbnail is required');
        setSaving(false);
        return;
      }

      if (editingVideo) {
        await axios.put(
          `${API_URL}/api/media/videos/${editingVideo.id}`,
          updatedForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Video updated');
      } else {
        await axios.post(
          `${API_URL}/api/media/videos`,
          updatedForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Video created');
      }
      setOpenDialog(false);
      setFileData({});
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!confirm('Delete this video?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/media/videos/${videoId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const addTag = () => {
    if (newTag.trim() && !form.tags.includes(newTag.trim())) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  // File Upload Handlers


  const getVideoType = (video) => {
    if (video.player_type === 'youtube') return 'YouTube';
    if (video.video_upload) return 'Uploaded';
    return 'External URL';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <VideoIcon className="w-8 h-8 text-[#0E6D2B]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Videos Management</h1>
              <p className="text-sm text-gray-600">Manage video library</p>
            </div>
          </div>
          <Button onClick={handleCreate} className="bg-[#0E6D2B]">
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0E6D2B]" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map(video => (
              <Card key={video.id}>
                <CardContent className="p-4">
                  <div className="relative mb-3">
                    <img src={getImageUrl(video.thumbnail_image)} alt={video.title} className="w-full h-40 object-cover rounded" />
                    <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      {video.player_type === 'youtube' ? <Youtube className="w-3 h-3" /> : <Film className="w-3 h-3" />}
                      {getVideoType(video)}
                    </div>
                  </div>
                  <p className="font-medium text-sm mb-1">{video.title}</p>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{video.description}</p>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(video)} size="sm" variant="outline" className="flex-1">
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button onClick={() => handleDelete(video.id)} size="sm" variant="destructive" className="flex-1">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Video Dialog */}
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingVideo ? 'Edit Video' : 'Add Video'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Video title"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Video Upload Path or Upload File</Label>
                  <div className="space-y-2">
                    <FileUploadInput
                      field={{ name: 'video_file', type: 'file', fileType: 'video' }}
                      value={fileData.video_file}
                      existingUrl={form.video_upload}
                      onChange={(files) => {
                        setFileData({ ...fileData, video_file: files });
                        // If file selected, clear URL
                        if (files && files.length > 0) {
                          setForm({ ...form, video_url: '' });
                        }
                      }}
                      onDeleteServerFile={() => { setForm({ ...form, video_upload: '' }); return true; }}
                    />
                  </div>
                </div>
                <div>
                  <Label>OR Video URL</Label>
                  <Input
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://youtube.com/... or direct URL"
                    disabled={!!(fileData.video_file && fileData.video_file.length > 0) || (form.video_upload && form.video_upload.length > 0)}
                  />
                  {(fileData.video_file || form.video_upload) && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ Video file uploaded/selected - URL disabled</p>
                  )}
                </div>
              </div>

              <div>
                <Label>Thumbnail Image *</Label>
                <FileUploadInput
                  field={{ name: 'thumbnail_image', type: 'file', fileType: 'image' }}
                  value={fileData.thumbnail_file}
                  existingUrl={form.thumbnail_image}
                  onChange={(files) => setFileData({ ...fileData, thumbnail_file: files })}
                  onDeleteServerFile={() => { setForm({ ...form, thumbnail_image: '' }); return true; }}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Video description"
                  rows={3}
                />
              </div>

              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  />
                  <Button onClick={addTag} size="sm" type="button">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.tags.map(tag => (
                    <span key={tag} className="bg-gray-200 px-2 py-1 rounded text-sm flex items-center gap-1">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="text-red-500 hover:text-red-700">×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Captions File (Optional)</Label>
                  <FileUploadInput
                    field={{ name: 'captions_file', type: 'file', fileType: 'document' }}
                    value={fileData.captions_file}
                    existingUrl={form.captions_file}
                    onChange={(files) => setFileData({ ...fileData, captions_file: files })}
                    onDeleteServerFile={() => { setForm({ ...form, captions_file: '' }); return true; }}
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="video_published"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="video_published">Published</Label>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button onClick={() => setOpenDialog(false)} variant="outline">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#0E6D2B]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Video
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
